/* eslint-disable react/no-danger */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/no-array-index-key */
'use client';
import classNames from 'classnames';
import { BaseTranslations } from '../../baseTranslations';
import useOpenRewardModal from '../../hooks/useOpenRewardModal';
import { ApprovalDegree, ApprovalOddsState } from '../../types/approvalOdds';
import {
  CmsProduct,
  GiftListItem,
} from '../../types/cmsProduct';
import { VerticalType } from '../../types/cmsTypes';
import { PushDataLayerFn } from '../../types/pushDataLayer';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import {
  currencyFormat,
  formatNumberWithSeparator,
} from '../../utils/helpers';
import { LoanSearchParams } from '../../utils/searchParams/loanSearchParams';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { tr } from '../../utils/translations/tr';
import { useCtaUrl } from '../ApplyCta';
import { AsideModal } from '../AsideModal';
import { CarInsuranceCtaBuilderInstance } from '../CarInsurance/CarInsuranceCtaBuilder';
import { EligibilityOffer } from '../ProductCard/types';
import { RichText } from '../RichText';
import { TravelInsuranceCtaType } from '../TravelInsuranceCtaBuilder';
import { formatRangeTitle } from './formatRangeTitle';
import { RewardAsideModalFooter } from './RewardAsideModalFooter';

type RewardAsideModalProps = {
  welcomeOffers: EligibilityOffer[];
  exclusiveOffers: EligibilityOffer[];
  product: CmsProduct;
  scrollRef?: React.Ref<HTMLDivElement>;
  loanSearchParams?: LoanSearchParams;
  carInsuranceCtaComponent: CarInsuranceCtaBuilderInstance | null;
  priceLine: React.ReactNode | null;
  keyFromComponent: string;
  vertical?: VerticalType;
  ctaLinkOverride?: string;
  pushDataLayer: PushDataLayerFn;
  travelInsuranceCtaComponent?: (
    travelInsuranceCtaType: TravelInsuranceCtaType,
  ) => JSX.Element | undefined;
  approvalOdds?: ApprovalDegree | null;
  approvalOddsState?: ApprovalOddsState | null;
};

// Verticals for which the "You're eligible to choose one reward" heading above
// the gift list is hidden. For insurance verticals the reward list is
// self-explanatory, so this lead-in line is intentionally omitted
// Add a vertical here to suppress the heading for that vertical only.
const VERTICALS_WITHOUT_ELIGIBLE_HEADING: VerticalType[] = [
  'TRAVEL_INSURANCE',
  'CAR_INSURANCE',
  'INSURANCE',
];

const RewardAsideModal: React.FC<RewardAsideModalProps> = ({
  exclusiveOffers,
  welcomeOffers,
  product,
  scrollRef,
  loanSearchParams,
  carInsuranceCtaComponent,
  priceLine,
  keyFromComponent,
  vertical,
  ctaLinkOverride,
  pushDataLayer,
  travelInsuranceCtaComponent,
  approvalOdds,
  approvalOddsState,
}) => {
  const { amount, tenure } = loanSearchParams ?? {};
  const context = useCmsClientContext();
  const { brand } = context;

  const isEligibleHeadingHidden = vertical
    ? VERTICALS_WITHOUT_ELIGIBLE_HEADING.includes(vertical)
    : false;

  const staticText = getClientTranslations(BaseTranslations, context);
  const { closeModal, rewardModalComponentKey, rewardModalProductId } =
    useOpenRewardModal();
  const ctaLink = useCtaUrl({
    product,
    ctaLinkOverride,
    loanParams: loanSearchParams,
  });

  // Determine the currency prefix from the offers
  const currency = currencyFormat(
    [...(product.welcome_offers ?? []), ...(product.exclusive_offers ?? [])]
      .flatMap((offer) => offer.list_of_gifts || [])
      .map((giftItem) => giftItem.gift?.gift_value?.prefix)
      .find((prefix) => prefix),
  );

  const upSectionTitle = tr(
    vertical === 'PERSONAL_LOANS'
      ? 'loan_amount_exclusive_bold'
      : 'travel_exclusive_bold',
    staticText,
    {
      product: product?.product_name ?? '',
      amount:
        formatNumberWithSeparator({
          value: amount ?? '',
          thousandsSeparator: ',',
        }) ?? '',
      tenure: tenure ?? '',
      brand: brand[0].toUpperCase() + brand.slice(1),
      currency: currency.length
        ? currency
        : brand === 'singsaver'
          ? 'SGD$'
          : brand === 'money101'
            ? 'NT$'
            : '',
    },
  );

  const upSectionTitleModified = () => {
    switch (vertical) {
      case 'PERSONAL_LOANS':
        return tenure && amount
          ? upSectionTitle
          : `<b>${product?.product_name}</b>`;
      case 'TRAVEL_INSURANCE':
        return upSectionTitle;
      default:
        return upSectionTitle;
    }
  };

  const renderOffers = (
    offers: EligibilityOffer[],
    type: 'welcome' | 'exclusive',
  ) => {
    const titleClasses = classNames('mhg-alpha-typography-bullet-1', {
      'text-fill/primary-dark': type === 'exclusive',
    });
    const sectionClasses = classNames(
      'rounded-radius/button px-spacing/padding/soft-12px py-spacing/gap/soft-12px',
      {
        'bg-fill/primary-light': type === 'exclusive',
        'bg-fill/alternative-1': type === 'welcome',
      },
    );

    const renderGifts = (giftList: GiftListItem[] | undefined) => {
      const grouped = giftList?.reduce(
        (acc, gift) => {
          const rangeTitle = formatRangeTitle(
            currency,
            gift.criteria_list,
            staticText,
          );

          const giftTitle = gift.gift?.gift_title ?? '';

          if (!rangeTitle) {
            acc.push({
              title: giftTitle,
              hasAmountRangeTitle: false,
              subitems: [],
            });
          } else {
            const existedItem = acc.find(
              (item) => item.hasAmountRangeTitle && item.title === rangeTitle,
            );
            if (existedItem) {
              existedItem.subitems.push(giftTitle);
            } else {
              acc.push({
                title: rangeTitle,
                subitems: [giftTitle],
                hasAmountRangeTitle: true,
              });
            }
          }

          return acc;
        },
        [] as {
          title: string;
          subitems: string[];
          hasAmountRangeTitle: boolean;
        }[],
      );

      return (
        <>
          {grouped?.map((item, index) => (
            <div key={index} className="flex flex-col gap-1 pl-3">
              {item.hasAmountRangeTitle ? (
                <li className="mhg-alpha-bullet-2 list-inside">
                  <span className="text-fill/primary-dark">{item.title}</span>
                  <ul className="flex list-disc flex-col gap-2 pl-10 pt-2">
                    {item.subitems.map((subitem, subIndex) => (
                      <li key={subIndex}>{subitem}</li>
                    ))}
                  </ul>
                </li>
              ) : (
                <li className="mhg-alpha-bullet-2">{item.title}</li>
              )}
            </div>
          ))}
        </>
      );
    };

    return (
      <div className="flex flex-col gap-y-gap/y-sm">
        {offers.length > 0
          ? offers.map((offer, offerIndex) => {
              const hasGifts = (offer.list_of_gifts?.length ?? 0) > 0;
              const hasIneligible = (offer.ineligible_list?.length ?? 0) > 0;
              const shouldRenderSection = hasGifts || hasIneligible;

              return (
                <div key={offerIndex}>
                  {shouldRenderSection && (
                    <div className="flex flex-col gap-spacing/gap/soft-12px rounded-radius/card p-spacing/gap/soft-12px bg-fill/card">
                      <div className={sectionClasses}>
                        <span className={titleClasses}>
                          {offer.offer_title}
                        </span>
                      </div>
                      {offer.introduction && (
                        <RichText>{offer.introduction}</RichText>
                      )}
                      <div className="flex flex-col gap-spacing/gap/soft-12px pt-spacing/gap/soft-12px">
                        {hasGifts && (
                          <>
                            {tenure && amount ? (
                              <div className="mhg-alpha-bullet-2">
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: upSectionTitleModified(),
                                  }}
                                />
                              </div>
                            ) : (
                              !isEligibleHeadingHidden && (
                                <div className="mhg-alpha-bullet-2">
                                  <span>{eligibleTitle}:</span>
                                </div>
                              )
                            )}
                            {renderGifts(offer.list_of_gifts)}
                          </>
                        )}

                        {hasIneligible && (
                          <>
                            <div className="mhg-alpha-bullet-2">
                              {tr('ineligible_choose_reward', staticText)}:
                            </div>
                            {renderGifts(offer.ineligible_list)}
                          </>
                        )}
                      </div>
                      {offer.description && (
                        <details>
                          <summary
                            className="mhg-alpha-typography-button-small cursor-pointer underline text-text/link"
                            style={{ listStyle: 'none' }}
                          >
                            {tr('show_term_cond', staticText)}
                          </summary>
                          <RichText>{offer.description}</RichText>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          : null}
      </div>
    );
  };

  const titleAsideModal = (): string => {
    switch (vertical) {
      case 'TRAVEL_INSURANCE':
        return tr('your_reward', staticText);
      case 'CAR_INSURANCE':
        return tr('your_reward', staticText);
      case 'PERSONAL_LOANS':
        return tr('loan_reward_provider_gift', staticText);
      default:
        return tr('insurance_reward_bank_gift', staticText);
    }
  };

  const eligibleTitle = (() => {
    switch (vertical) {
      case 'CAR_INSURANCE':
        return tr('eligible_choose_reward_CI', staticText);
      case 'TRAVEL_INSURANCE':
        return tr('eligible_choose_reward_TI', staticText);
      default:
        return tr('eligible_choose_reward_without_tenure_and_amount', staticText);
    }
  })();

  return (
    <AsideModal
      size="small"
      isOpen={
        rewardModalProductId === product.uid &&
        keyFromComponent === rewardModalComponentKey
      }
      onClose={() => closeModal()}
    >
      <div className="relative flex h-screen flex-col justify-between">
        <div>
          <div className="border border-borders/default px-padding/x py-spacing/padding/y-sm alpha-subheading-1">
            {titleAsideModal()}
          </div>
          <div className="flex flex-col gap-y-gap/y-sm px-spacing/padding/x pt-spacing/padding/y">
            <div
              className="mhg-alpha-body-2"
              dangerouslySetInnerHTML={{
                __html: upSectionTitleModified() ?? '',
              }}
            />

            {renderOffers(exclusiveOffers, 'exclusive')}
            <div ref={scrollRef}>{renderOffers(welcomeOffers, 'welcome')}</div>
          </div>
        </div>
        <div className="sticky bottom-0 p-0">
          <RewardAsideModalFooter
            key={product.product_name}
            product={product}
            ctaLink={ctaLink}
            pushDataLayer={pushDataLayer}
            CarInsuranceCtaComponent={carInsuranceCtaComponent}
            priceLine={priceLine}
            travelInsuranceCtaComponent={travelInsuranceCtaComponent}
            approvalOdds={approvalOdds}
            approvalOddsState={approvalOddsState}
          />
        </div>
      </div>
    </AsideModal>
  );
};

export default RewardAsideModal;
